import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { format } from 'date-fns';

/**
 * AnalyticsChart - Atomic component for rendering different chart types
 */
const AnalyticsChart = ({ 
  title, 
  data, 
  type = 'area',
  dataKeys = [],
  height = 300
}) => {
  const formatDate = (value) => {
    try {
      return format(new Date(value), 'MMM d');
    } catch {
      return value;
    }
  };

  const formatTooltipDate = (value) => {
    try {
      return format(new Date(value), 'MMM d, yyyy');
    } catch {
      return value;
    }
  };

  const tooltipStyle = {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  };

  const colors = {
    page_views: '#1e3a8a',
    unique_visitors: '#10b981'
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    const commonAxisProps = {
      xAxis: (
        <XAxis 
          dataKey="date" 
          tick={{ fontSize: 12 }}
          tickFormatter={formatDate}
        />
      ),
      yAxis: <YAxis tick={{ fontSize: 12 }} />,
      grid: <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />,
      tooltip: (
        <Tooltip 
          contentStyle={tooltipStyle}
          labelFormatter={formatTooltipDate}
        />
      ),
      legend: <Legend />
    };

    switch (type) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              {dataKeys.map((key) => (
                <linearGradient key={`gradient-${key}`} id={`color${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors[key] || '#1e3a8a'} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={colors[key] || '#1e3a8a'} stopOpacity={0}/>
                </linearGradient>
              ))}
            </defs>
            {commonAxisProps.grid}
            {commonAxisProps.xAxis}
            {commonAxisProps.yAxis}
            {commonAxisProps.tooltip}
            {commonAxisProps.legend}
            {dataKeys.map((key) => (
              <Area 
                key={key}
                type="monotone" 
                dataKey={key} 
                stroke={colors[key] || '#1e3a8a'} 
                fillOpacity={1} 
                fill={`url(#color${key})`}
                name={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              />
            ))}
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            {commonAxisProps.grid}
            {commonAxisProps.xAxis}
            {commonAxisProps.yAxis}
            {commonAxisProps.tooltip}
            {commonAxisProps.legend}
            {dataKeys.map((key) => (
              <Bar 
                key={key}
                dataKey={key} 
                fill={colors[key] || '#1e3a8a'} 
                name={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        );

      case 'line':
        return (
          <LineChart {...commonProps}>
            {commonAxisProps.grid}
            {commonAxisProps.xAxis}
            {commonAxisProps.yAxis}
            {commonAxisProps.tooltip}
            {commonAxisProps.legend}
            {dataKeys.map((key) => (
              <Line 
                key={key}
                type="monotone" 
                dataKey={key} 
                stroke={colors[key] || '#1e3a8a'}
                strokeWidth={2}
                dot={{ fill: colors[key] || '#1e3a8a', strokeWidth: 2 }}
                name={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              />
            ))}
          </LineChart>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-bold text-[#1e3a8a]">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ height: `${height}px`, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

AnalyticsChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    page_views: PropTypes.number,
    unique_visitors: PropTypes.number
  })).isRequired,
  type: PropTypes.oneOf(['area', 'bar', 'line']),
  dataKeys: PropTypes.arrayOf(PropTypes.string),
  height: PropTypes.number
};

AnalyticsChart.defaultProps = {
  type: 'area',
  dataKeys: ['page_views', 'unique_visitors'],
  height: 300
};

export default AnalyticsChart;
